/**
 * TEMPORARY NAVIGATION PATCHES
 * -----------------------------------------------------------------------------
 * Context
 * - The backend still returns legacy menu structures (e.g. a top-level parent
 *   named "Setup" with children under "/Setup/..."), while our new UI expects a
 *   top-level "Configuration" section (PascalCase advisor routes like
 *   "/Advisor/Configuration/..."), plus a normalized Documents section.
 *
 * Why these patches exist
 * - Until the backend updates its menu payload to match the new IA and route
 *   scheme, we apply minimal client-side adjustments so the sidebar aligns with
 *   routes in src/config/routes.tsx.
 * - These edits are intentionally scoped and reversible. Prefer central remaps
 *   in src/constants/navigation.ts when possible; remove these once backend
 *   outputs the new structure/paths.
 *
 * What this file changes (high level)
 * 1) Setup → Configuration promotion: promote/rename the legacy "Setup" parent
 *    to a top-level "Configuration" and replace its children with the exact,
 *    router-matching advisor routes (Client Portal, Integrations, Team Member).
 * 2) Documents ordering/gating: enforce a stable child order (Document Vault,
 *    Reports, Notes, optionally Intelligence) and gate Intelligence by a
 *    specific AdvisorGroupId.
 *    Intelligence is formerly Ask Your Vault, thus the path name.
 *
 * Clients: no structural patch. Visibility is governed only by the backend
 * menu payload and NOT_YET_IMPLEMENTED_ROUTES.
 *
 * Not done here
 * - We do NOT normalize case/variants of routes here anymore. All routes should
 *   be PascalCase upstream (handled by central remaps and the router).
 * - We do NOT hide items here (that is handled by filterDeprecatedMenuItems via
 *   DEPRECATED_ROUTES, when needed). This file is strictly structural.
 */
import { NavigationItem } from '@/config/navigations';
import { Role } from '@/models/AccountEnums';
import { AppUserInfo } from '@/models/AppViewModel';
import { filterDeprecatedMenuItems } from '@/utils/navigationUtils';
import { paths } from '@/constants/paths';
import { resolveAdvisorNavRoute } from '@/modules/advisor/utils/advisorNavigation';

type MutableNavigationItem = NavigationItem & {
  children?: MutableNavigationItem[];
};

/**
 * Create a shallow clone of a navigation node and recursively clone children.
 * Rationale: avoid mutating the original tree (often sourced from store/props).
 */
function cloneItem(item: NavigationItem): MutableNavigationItem {
  return {
    ...item,
    children: item.children ? item.children.map(cloneItem) : undefined,
  };
}

function routeEquals(a?: string, b?: string) {
  return (
    (a || '').replace(/\/$/, '').toLowerCase() ===
    (b || '').replace(/\/$/, '').toLowerCase()
  );
}

/**
 * Normalized trailing segment of a Configuration sub-route ("/Setup/Legal" ->
 * "legal"). Used to match backend-sent Setup children against the sub-routes
 * we support, both here and in validateByUrl, so nav and permissions gate the
 * same way. The backend sends "/Setup/TeamMembers" for the page routed at
 * "team-member", so both spellings share one key.
 */
const CONFIG_LEAF_KEY_ALIASES: Partial<Record<string, string>> = {
  teammembers: 'teammember',
};

export function configLeafKey(route?: string): string {
  const trailing = (route ?? '').split('/').filter(Boolean).pop() ?? '';
  const key = trailing.toLowerCase().replace(/[^a-z]/g, '');
  return CONFIG_LEAF_KEY_ALIASES[key] ?? key;
}

/**
 * Returns Configuration sub-route nav items for the sidebar. Reused in validateByUrl (routes normalized there).
 * IMPORTANT: Keep these in sync with src/config/routes.tsx
 */
export function getConfigSubrouteNavigationItems(
  role: Role,
): MutableNavigationItem[] {
  const isAdvisor = role === Role.Advisor;
  const isFirm = role === Role.Firm;
  const base = isAdvisor ? paths.advisor.configuration.index : '/configuration';
  const desiredChildren: MutableNavigationItem[] = [
    // TODO(NU-940): Re-enable the remaining Configuration tabs once their pages are complete
    // Advisor & Client Portals hidden from sidebar + SubNavigationTabRow until ready.
    // ...(isAdvisor
    //   ? [
    //       {
    //         text: 'Advisor & Client Portals',
    //         route: paths.advisor.configuration.advisorAndClientPortals,
    //       },
    //     ]
    //   : []),
    ...(isFirm ? [{ text: 'Dashboard', route: `${base}/dashboard` }] : []),
    // Managed Accounts — advisor-only. Text is the display term
    // so the firm-configurable label override (menuNameKeyMap) applies.
    ...(isAdvisor
      ? [
          {
            text: 'Managed Accounts',
            route: paths.advisor.configuration.managedAccounts.index,
          },
        ]
      : []),
    {
      text: 'Legal',
      route: isAdvisor
        ? paths.advisor.configuration.legal.index
        : `${base}/legal`,
    },
    {
      text: 'Integrations',
      route: isAdvisor
        ? paths.advisor.configuration.integrations
        : `${base}/integrations`,
    },
    ...(isAdvisor
      ? [
          {
            text: 'Team Members',
            route: paths.advisor.configuration.teamMember,
          },
        ]
      : []),
  ];
  if (!isAdvisor) {
    desiredChildren.push({ text: 'Terms', route: `${base}/terms` });
  }
  return desiredChildren;
}

/**
 * Configuration sub-pages that a bare Setup grant does not include. Each one
 * needs its own backend grant, because its endpoint does not check the menu.
 */
const EXPLICIT_GRANT_CONFIG_LEAF_KEYS: ReadonlySet<string> = new Set([
  'teammember',
]);

export function isDefaultConfigSubroute(route?: string): boolean {
  return !EXPLICIT_GRANT_CONFIG_LEAF_KEYS.has(configLeafKey(route));
}

/** Normalized paths that count as "has Configuration/Setup" in the backend menu. */
export const CONFIG_PARENT_PATHS = [
  '/configuration',
  '/setup',
  '/advisor/configuration',
  '/advisor/setup',
] as const;

// ensureChild helper removed as unused

/**
 * applyInsertions: structural edits that cannot be represented by a simple
 * remap (e.g., rebuilding children, gating features, ensuring dropdowns).
 */
function applyInsertions(
  items: MutableNavigationItem[],
  role: Role,
  _options?: { userInfo?: AppUserInfo },
): MutableNavigationItem[] {
  const cloned = items.map(cloneItem);

  // 1) Promote legacy Setup → top-level Configuration (router-matching routes)
  //    Why: Backend still emits a parent "Setup" with children under /Setup/...
  //         Our UI (routes.tsx) exposes a top-level Configuration section.
  //    How: Detect the legacy parent and replace it with the exact children our
  //         routes support for the active role. Remove duplicate top-level
  //         Configuration nodes to keep the sidebar tidy.
  {
    const setupIndex = cloned.findIndex(
      (i) =>
        i.text === 'Setup' ||
        routeEquals(i.route, '/advisor/setup') ||
        routeEquals(i.route, '/setup') ||
        // ROUTE_REMAPPINGS turns a bare /Setup grant into a childless
        // /Configuration item at ingestion — treat it as a bare Setup parent
        // so it still gets the default sub-pages. A Configuration node WITH
        // children (remapped-Setup shape) keeps the backend's enumeration
        // and intentionally bypasses this patch.
        ((i.children ?? []).length === 0 &&
          (routeEquals(i.route, '/advisor/configuration') ||
            routeEquals(i.route, '/configuration'))) ||
        (i.children ?? []).some(
          (c) =>
            (c.route ?? '').toLowerCase().startsWith('/advisor/setup/') ||
            (c.route ?? '').toLowerCase().startsWith('/setup/'),
        ),
    );
    if (setupIndex >= 0) {
      const setup = cloned[setupIndex];

      const isAdvisor = role === Role.Advisor;
      const base = isAdvisor
        ? paths.advisor.configuration.index
        : '/configuration';

      // Firms can disable individual Configuration sub-pages (same LB-5
      // mechanism as Documents/EUD-465): when the backend enumerates Setup
      // children, only keep the sub-routes it sent — a disabled sub-page must
      // not be re-injected. A bare Setup parent (no children) keeps the
      // default list so navigation stays usable.
      const backendChildren = setup.children ?? [];
      const sentLeafKeys = new Set(
        backendChildren
          .map((child) => configLeafKey(child.route))
          .filter(Boolean),
      );
      const desiredChildren = getConfigSubrouteNavigationItems(role);
      const enabledChildren =
        sentLeafKeys.size === 0
          ? desiredChildren.filter((child) =>
              isDefaultConfigSubroute(child.route),
            )
          : desiredChildren.filter((child) =>
              sentLeafKeys.has(configLeafKey(child.route)),
            );

      if (enabledChildren.length === 0) {
        // Backend enumerated children but none we support — drop the parent
        // so it doesn't render empty (mirrors the Documents patch).
        cloned.splice(setupIndex, 1);
      } else {
        setup.text = 'Configuration';
        setup.route = base;
        setup.children = enabledChildren;

        // Remove any other top-level Configuration nodes to avoid duplicates
        for (let i = cloned.length - 1; i >= 0; i--) {
          if (i !== setupIndex && cloned[i].text === 'Configuration') {
            cloned.splice(i, 1);
          }
        }
      }
    }
  }

  // 1b) Advisor: reparent a legacy top-level Team grant under Configuration.
  //    Why: the backend can send /Team (or /Tools/Team, remapped to /Team).
  //         A ROUTE_REMAPPINGS entry would also move the investor Team page,
  //         so the advisor tree moves it here. Team Members then shows in the
  //         Configuration section and its tab row, not at the sidebar root.
  //         It runs after step 1, so a bare Setup keeps its default children.
  if (role === Role.Advisor) {
    const teamIndex = cloned.findIndex((i) =>
      routeEquals(i.route, '/advisor/team'),
    );
    if (teamIndex >= 0) {
      for (let i = cloned.length - 1; i >= 0; i--) {
        if (routeEquals(cloned[i].route, '/advisor/team')) cloned.splice(i, 1);
      }
      const teamMembers: MutableNavigationItem = {
        text: 'Team Members',
        route: paths.advisor.configuration.teamMember,
      };
      const config = cloned.find(
        (i) =>
          i.text === 'Configuration' ||
          routeEquals(i.route, paths.advisor.configuration.index),
      );
      const configChildren = config?.children ?? [];
      if (!config) {
        cloned.splice(Math.min(teamIndex, cloned.length), 0, {
          text: 'Configuration',
          route: paths.advisor.configuration.index,
          children: [teamMembers],
        });
      } else if (
        !configChildren.some(
          (c) => configLeafKey(c.route) === configLeafKey(teamMembers.route),
        )
      ) {
        config.children = [...configChildren, teamMembers];
      }
    }
  }

  // 1c) Keep one Configuration child per sub-page.
  //    Why: ROUTE_REMAPPINGS maps two grants to Team Members
  //         (/Setup/TeamMembers and /Advisor/Team). A Configuration node that
  //         already has children skips step 1, so both entries reach here.
  {
    const config = cloned.find((i) => i.text === 'Configuration');
    if (config?.children) {
      const seenLeafKeys = new Set<string>();
      config.children = config.children.filter((child) => {
        const key = configLeafKey(child.route);
        if (!key) return true;
        if (seenLeafKeys.has(key)) return false;
        seenLeafKeys.add(key);
        return true;
      });
    }
  }

  // 2) Documents: enforce stable order; only include sub-pages the backend sent.
  //    Why: firm admins can disable individual sub-pages via legacy
  //         Configuration → Pages (LB-5). When a sub-page is disabled, the
  //         backend omits it from UserInfo.Menu, so we must NOT re-inject it.
  //         Previous implementation hardcoded both Document Vault and Reports,
  //         causing disabled sub-pages to appear in the sidebar and trigger
  //         "Access Denied" on click (EUD-465).

  // Find Documents parent by text or by route pattern to be robust against backend order/labels
  const parentIndex = cloned.findIndex(
    (i) =>
      i.text === 'Documents' ||
      routeEquals(i.route, '/advisor/documents') ||
      routeEquals(i.route, '/Documents') ||
      (i.children ?? []).some(
        (c) =>
          (c.route ?? '').toLowerCase().startsWith('/advisor/documents/') ||
          (c.route ?? '').startsWith('/Documents/'),
      ),
  );

  if (parentIndex >= 0) {
    const parent = cloned[parentIndex];

    // Determine base prefix from active role; always role-prefixed.
    // Role enum values are already correctly cased (e.g. 'Firm', 'Advisor').
    const isAdvisor = role === Role.Advisor;
    const base = `/${role}/Documents`;

    // Canonical sub-pages in display order. We only render the ones present
    // in the backend payload — disabled sub-pages must stay hidden. `suffix` is
    // matched against the (still PascalCase-segmented) backend route; advisor
    // emits the clean `/advisor/*` path, other roles keep `${base}${suffix}`.
    const CANONICAL_SUBPAGES: Array<{
      text: string;
      suffix: string;
      advisorRoute: string;
    }> = [
      {
        text: 'Document Vault',
        suffix: '/DocumentVault',
        advisorRoute: paths.advisor.documents.documentVault,
      },
      {
        text: 'Reports',
        suffix: '/Reports',
        advisorRoute: paths.advisor.reports,
      },
    ];

    const backendChildren = parent.children ?? [];
    const backendHasSuffix = (suffix: string): boolean =>
      backendChildren.some((child) => {
        const childRoute = (child.route ?? '').replace(/\/$/, '').toLowerCase();
        return childRoute.endsWith(suffix.toLowerCase());
      });

    const ordered: MutableNavigationItem[] = CANONICAL_SUBPAGES.filter((sp) =>
      backendHasSuffix(sp.suffix),
    ).map((sp) => ({
      text: sp.text,
      route: isAdvisor ? sp.advisorRoute : `${base}${sp.suffix}`,
    }));

    if (ordered.length === 0) {
      // All sub-pages disabled — drop the parent so it doesn't render empty.
      cloned.splice(parentIndex, 1);
    } else {
      parent.text = 'Documents'; // keep parent label stable before translation
      parent.route = ordered[0].route; // parent routes to first tab
      parent.children = ordered;
    }
  }

  // Clients visibility is governed solely by the backend menu payload and the
  // NOT_YET_IMPLEMENTED_ROUTES hide filter — no structural collapse here. The
  // backend-sent children (including the self-referential "Clients" section
  // index child) pass through unchanged.
  //
  // Label patch: when the backend sends Clients as a parent WITH children
  // (dropdown shape), rename the parent label to "Relationships". A childless
  // Clients grant (leaf link) keeps its label. Only the top-level parent is
  // renamed — the self-referential section-index child has no children, so it
  // is left as "Clients".
  //
  // NOTE: for the Advisor role this label is re-decided authoritatively AFTER
  // the hide filter by finalizeAdvisorClientsSection (route-based, ignoring the
  // self-referential index child), so the value set here is only the pre-filter
  // default and matters for non-advisor roles. See advisorNavigation.ts.
  {
    const clientsIndex = cloned.findIndex(
      (i) =>
        (i.text === 'Clients' ||
          routeEquals(i.route, '/clients') ||
          routeEquals(i.route, '/advisor/clients')) &&
        (i.children ?? []).length > 0,
    );
    if (clientsIndex >= 0) {
      cloned[clientsIndex].text = 'Relationships';
    }
  }

  // 4) Managed Accounts is a single section; its sub-tabs are
  //    rendered by the section page's own nav, not the sidebar. The legacy
  //    backend node (/Setup/Accounts) ships Account Tags / Asset Classifications
  //    / AUM-Held Away as children. After the /Setup/Accounts ->
  //    /Configuration/ManagedAccounts remap, the "remapped Setup" shape keeps
  //    the backend's enumeration and bypasses the child-rebuild in step 1, so
  //    those grandchildren survive — and the sidebar renders any item WITH
  //    children as an expand toggle instead of a link. Force it to a leaf.
  {
    const stripManagedAccountsChildren = (
      nodes: MutableNavigationItem[],
    ): void => {
      nodes.forEach((node) => {
        if (configLeafKey(node.route) === 'managedaccounts') {
          node.children = undefined;
          return;
        }
        if (node.children) {
          stripManagedAccountsChildren(node.children);
        }
      });
    };
    stripManagedAccountsChildren(cloned);
  }

  return cloned;
}

// Removed route normalization to enforce PascalCase-only routes upstream

/**
 * Entry point for navigation patches.
 * Pipeline:
 * 1) applyInsertions: structural fixes (Setup→Configuration, Documents, Clients)
 * 2) filterDeprecatedMenuItems: optional deprecation stage (none by default)
 * Label standardization (e.g. "AUM/Held Away" → "AUM & Held Away") is handled
 * by useLocalization + menuNameKeyMap when nav items are translated in the UI.
 */
export function applyMenuPatches(
  items: NavigationItem[] | undefined,
  role: Role,
  options?: { userInfo?: AppUserInfo },
): NavigationItem[] {
  if (!items) return [];

  // 1) Insert/mutate items (scoped behavior)
  let patched = applyInsertions(items, role, options);

  // 2) Deprecate/hide unwanted routes (none by default)
  const deprecatedRoutes: string[] = [];
  patched = filterDeprecatedMenuItems(patched, deprecatedRoutes);

  // 3) Advisor route-normalization (catch-all): rewrite any remaining
  //    backend-derived advisor route to its canonical `/advisor/*` value so the
  //    sidebar links match the router and pass PagePermissionGuard.
  if (role === Role.Advisor) {
    const canonicalize = (nodes: NavigationItem[]): NavigationItem[] =>
      nodes.map((node) => ({
        ...node,
        route: resolveAdvisorNavRoute(node.route) ?? node.route,
        children: node.children ? canonicalize(node.children) : undefined,
      }));
    patched = canonicalize(patched);
  }

  return patched;
}
